import { call, put, select, spawn } from 'redux-saga/effects';
import { WebSocketMessage } from '../../../models/WebSocket';

import {
  getCurrentFolderRedirect,
  getProject,
  RootReducerState,
} from '../../../apps/main/rootReducer';
import { Project } from '../../../models/Project';
import { wsFetchDocumentCopiedOrMoved } from '../actions/ws';
import { DriveItemId, GroupId, ProjectId } from '../../../models/Types';
import { apiFetchDocument } from '../api';
import {
  driveItemDeleted,
  DriveItemsFetchContext,
  fetchDriveItem,
  fetchDriveItemsSagaAction,
} from '../actions';
import handleDocumentCreated from './handleDocumentCreated';
import handleArchiveUnzipped from './handleArchiveUnzipped';
import { isDriveItemFolder } from '../util';
import handleDocumentFromTemplateCreated from './handleDocumentFromTemplateCreated';
import { SavedDocumentFromTemplateDto } from '../../../models/Document';
import {
  handleDocumentZipError,
  handleDocumentZipped,
  handleDocumentZipping,
} from './handleDocumentZip';

export declare type DocumentMessageTypes =
  | 'reportCreated'
  | 'documentCreated'
  | 'documentCopiedOrMoved'
  | 'archiveUnzipped'
  | 'archiveUnzipping'
  | 'archiveZipping'
  | 'archiveZipped'
  | 'archiveZipError';

export declare type DriveFavoriteMessageTypes =
  | 'driveFavoriteCreated'
  | 'driveFavoriteUpdated'
  | 'driveFavoriteDeleted';

export function* handleDocumentMessage(message: WebSocketMessage) {
  try {
    const project: Project = yield select<(state: RootReducerState) => Project>(
      (state) => getProject(state, message.projectId)
    );
    switch (message.type) {
      case 'documentCopiedOrMoved': {
        if (message.object) {
          yield spawn(
            handleCopiedOrMovedDocuments,
            message.object.destinationDriveItemId,
            message.object.destinationGroupId,
            message.projectId,
            message.object.sourceDriveItemId,
            message.object.sourceGroupId
          );
        }
        break;
      }
      case 'archiveUnzipped': {
        yield spawn(handleArchiveUnzipped, message, project);
        break;
      }
      case 'documentCreated':
      case 'reportCreated': {
        yield spawn(handleDocumentCreated, message, project);
        break;
      }

      case 'archiveZipping': {
        yield spawn(handleDocumentZipping, message);
        break;
      }
      case 'archiveZipped': {
        yield spawn(handleDocumentZipped, message, message.projectId);
        break;
      }
      case 'archiveZipError': {
        yield spawn(handleDocumentZipError, message);
      }
    }
  } catch (error) {
    console.error('Error in websocket flow - handleDocumentMessage', error);
  }
}

export function* handleDocumentFromTemplateCreatedMessage(
  message: WebSocketMessage
) {
  try {
    const savedDFT = message.type as any as SavedDocumentFromTemplateDto;
    const project: Project = yield select<(state: RootReducerState) => Project>(
      (state) => getProject(state, savedDFT.projectId)
    );
    yield spawn(
      handleDocumentFromTemplateCreated,
      message.type as any,
      project
    );
  } catch (error) {
    console.error(
      'Error in websocket flow - handleDocumentFromTemplateCreated',
      error
    );
  }
}

export function* handleDocumentDriveItemChangedMessage(
  message: WebSocketMessage
) {
  const {
    objectType: projectId,
    object: parentDriveItemId,
    destinationProjectId: action,
    projectId: driveItemId,
  } = message;

  const project: Project = yield select<(state: RootReducerState) => Project>(
    (state) => getProject(state, projectId)
  );
  if (!project) return;

  switch (action) {
    case 'created':
    case 'updated':
      yield put(
        fetchDriveItem(project.groupId, driveItemId, parentDriveItemId)
      );
      break;

    case 'deleted':
      yield put(
        driveItemDeleted(driveItemId, parentDriveItemId, project.groupId)
      );
      break;

    default:
      break;
  }
}

function* handleCopiedOrMovedDocuments(
  destinationDriveItemId: DriveItemId,
  destinationGroupId: GroupId,
  destinationProjectId: ProjectId,
  sourceDriveItemId: DriveItemId,
  sourceGroupId: GroupId
) {
  const temporaryId: string = yield select<(state: RootReducerState) => string>(
    (state) => getCurrentFolderRedirect(state, sourceDriveItemId)
  );

  const { data } = yield call(
    apiFetchDocument,
    destinationGroupId,
    destinationDriveItemId
  );
  if (data) {
    if (isDriveItemFolder(data)) {
      yield put(
        fetchDriveItemsSagaAction(
          destinationProjectId,
          destinationGroupId,
          destinationDriveItemId,
          false,
          DriveItemsFetchContext.Other
        )
      );
    }
    yield put(
      wsFetchDocumentCopiedOrMoved(
        data,
        destinationDriveItemId,
        destinationGroupId,
        sourceDriveItemId,
        sourceGroupId,
        temporaryId
      )
    );
  }
}
